// The phone and tablet landing is a pager of four full-stage slides: the hero
// (Home), then Pricing, Contact and Videos. The header's tabs are its index,
// so it needs no dots; the brand bar returns to Home. The slides host their
// content directly; the desktop landing opens the same content in windows.

import type { LandingWorkspaceAction } from "@/src/features/landing/workspaceActionState";

export const MOBILE_LANDING_SLIDES = ["home", "pricing", "contact", "videos"] as const;

export type MobileLandingSlide = (typeof MOBILE_LANDING_SLIDES)[number];

/** A slide the landing is asked to show (a restored locale switch, for one). */
export type MobileLandingSlideRequest = {
  id: number;
  slide: MobileLandingSlide;
};

export function mobileLandingSlideIndex(slide: MobileLandingSlide): number {
  return MOBILE_LANDING_SLIDES.indexOf(slide);
}

/** The desktop window each slide past Home stands in for; its nav key, too. */
export const MOBILE_LANDING_SLIDE_ACTION = {
  pricing: "Pricing",
  contact: "Contact",
  videos: "Video",
} as const satisfies Record<Exclude<MobileLandingSlide, "home">, LandingWorkspaceAction>;

/** The desktop window that shows a slide's content, or null for Home. */
export function mobileLandingActionForSlide(slide: MobileLandingSlide): LandingWorkspaceAction | null {
  return slide === "home" ? null : MOBILE_LANDING_SLIDE_ACTION[slide];
}

/**
 * The slide that shows a desktop window's content, or null where the phone
 * landing has none: Language still opens its window, and Download is not
 * offered on a phone or tablet (installing the apps needs a paid account).
 */
export function mobileLandingSlideForAction(action: LandingWorkspaceAction): MobileLandingSlide | null {
  switch (action) {
    case "Pricing":
      return "pricing";
    case "Contact":
      return "contact";
    case "Video":
      return "videos";
    default:
      return null;
  }
}

/**
 * The slide nearest the scroller's position. A right-to-left page scrolls
 * to negative offsets, so only the distance counts.
 */
export function nearestMobileLandingSlide(scrollLeft: number, slideWidth: number): number {
  if (!(slideWidth > 0)) return 0;
  const index = Math.round(Math.abs(scrollLeft) / slideWidth);
  return Math.min(MOBILE_LANDING_SLIDES.length - 1, Math.max(0, index));
}

/** The hero still shows any part of itself: its sphere keeps breathing until it is fully off the stage. */
export function mobileLandingHomeInView(scrollLeft: number, slideWidth: number): boolean {
  return Math.abs(scrollLeft) < slideWidth - 1;
}

/**
 * Where an arrow key (or Home and End) moves the pager from `index`, or null
 * for any other key. In a right-to-left page the next slide is on the left.
 */
export function mobileLandingSlideForKey(key: string, index: number, rtl: boolean): number | null {
  const last = MOBILE_LANDING_SLIDES.length - 1;
  const forward = rtl ? "ArrowLeft" : "ArrowRight";
  const back = rtl ? "ArrowRight" : "ArrowLeft";
  if (key === forward) return Math.min(last, index + 1);
  if (key === back) return Math.max(0, index - 1);
  if (key === "Home") return 0;
  if (key === "End") return last;
  return null;
}

/**
 * What carries over when the landing switches layouts (a tablet turned past
 * the desktop width, say): the desktop's Pricing, Contact or Videos window
 * becomes its slide, a slide becomes its window, and an open Language window
 * stays a window. Each layout mounts its own workspace and pager, so whatever
 * does not carry must be cleared, or the fresh tree would replay a stale
 * request (a Pricing window popping over Home after a swipe back).
 */
export type LandingLayoutCarry = {
  /** The window the new layout's workspace opens, or null to clear its command. */
  action: LandingWorkspaceAction | null;
  /** The slide the new phone landing shows, or null to clear its request. */
  slide: MobileLandingSlide | null;
};

export function landingLayoutCarry({
  toMobile,
  openAction,
  mobileSlide,
}: {
  toMobile: boolean;
  /** The window on screen at the switch, if any. */
  openAction: LandingWorkspaceAction | null;
  /** The slide on stage at the switch (only read when leaving the phone landing). */
  mobileSlide: MobileLandingSlide;
}): LandingLayoutCarry {
  // Language is a window in both layouts, and the latest thing the visitor opened.
  if (openAction === "Language") return { action: "Language", slide: null };
  if (toMobile) return { action: null, slide: openAction ? mobileLandingSlideForAction(openAction) : null };
  return { action: mobileLandingActionForSlide(mobileSlide), slide: null };
}
